import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useRef } from 'react'
import { useQuery as useConvexQuery, useAction } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import { instrumentalGenerationSchema, validateSafe } from '@/lib/validation'
import { useNativeAudioPlayer } from '@/hooks/useNativeAudioPlayer'

export const Route = createFileRoute('/app/instrumental')({
  component: InstrumentalPage,
})

function InstrumentalPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Please sign in to generate instrumental music</div>
      </div>
    )
  }

  return <InstrumentalPageContent userId={currentUser._id} />
}

function InstrumentalPageContent({ userId }: { userId: Id<'users'> }) {
  // Query recent instrumental generations
  const recentGenerations = useConvexQuery(api.tempolor.listByUser, {
    userId,
    limit: 10,
  })

  // Filter for instrumental type only
  const instrumentalGenerations = recentGenerations?.filter((gen) => gen.type === 'instrumental')

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Instrumental Music Generation</h1>
          <p className="text-gray-600">
            Generate AI-powered instrumental music without vocals using Tempolor
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Generation Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <InstrumentalForm userId={userId} />
            </div>
          </div>

          {/* Sidebar - Recent Generations */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Instrumentals</h2>
              {instrumentalGenerations === undefined ? (
                <p className="text-gray-500 text-sm">Loading...</p>
              ) : instrumentalGenerations.length === 0 ? (
                <p className="text-gray-500 text-sm">No instrumentals generated yet</p>
              ) : (
                <div className="space-y-3">
                  {instrumentalGenerations.map((gen) => (
                    <GenerationCard key={gen._id} generation={gen} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function InstrumentalForm({ userId }: { userId: Id<'users'> }) {
  const [prompt, setPrompt] = useState('')
  const [model, setModel] = useState('TemPolor i3.5') // Default instrumental model per Tempolor docs
  const [isGenerating, setIsGenerating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [lastGeneration, setLastGeneration] = useState<{
    generationId: Id<'temporGenerations'>
    temporRequestId: string
    prompt: string
    model: string
  } | null>(null)

  const generateInstrumental = useAction(api.tempolor.generateInstrumental)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)
    setLastGeneration(null)

    // Validate input with Zod
    const validation = validateSafe(instrumentalGenerationSchema, {
      prompt: prompt.trim(),
      model,
    })

    if (!validation.success) {
      setValidationError(validation.error)
      return
    }

    setIsGenerating(true)
    try {
      const result = await generateInstrumental({
        userId,
        ...validation.data,
      })

      // Store generation details to show in UI
      setLastGeneration({
        generationId: result.generationId,
        temporRequestId: result.temporRequestId,
        prompt: validation.data.prompt,
        model: validation.data.model,
      })

      setPrompt('')
    } catch (error) {
      console.error('Failed to generate instrumental:', error)
      setValidationError(error instanceof Error ? error.message : 'Generation failed')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Instrumental</h3>
        <p className="text-sm text-gray-600 mb-4">
          Describe the style and mood of instrumental music you want to create. The AI will
          generate music without any vocals.
        </p>
      </div>

      {validationError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{validationError}</p>
        </div>
      )}

      {lastGeneration && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-blue-900 font-semibold mb-2">Generation Started</div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-blue-700 font-medium">Request ID:</span>{' '}
              <span className="text-blue-900 font-mono">{lastGeneration.temporRequestId}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Prompt:</span>{' '}
              <span className="text-blue-900">{lastGeneration.prompt}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Model:</span>{' '}
              <span className="text-blue-900">{lastGeneration.model}</span>
            </div>
            <div className="text-blue-700 text-xs pt-2 border-t border-blue-200 mt-2">
              Check the sidebar for updates. The instrumental will appear when generation is
              complete.
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Prompt <span className="text-red-500">*</span>
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g., Upbeat electronic dance music with energetic synths and driving bassline"
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Describe the genre, style, mood, and instruments you want in your instrumental music
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="TemPolor i3.5">TemPolor i3.5 (Latest - Recommended)</option>
          <option value="TemPolor i3.0">TemPolor i3.0</option>
          <option value="TemPolor i2.0">TemPolor i2.0</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={isGenerating || !prompt.trim()}
        className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        {isGenerating ? 'Generating...' : 'Generate Instrumental'}
      </button>
    </form>
  )
}

function GenerationCard({
  generation,
}: {
  generation: {
    _id: Id<'temporGenerations'>
    type: string
    status: string
    prompt?: string
    audioUrl?: string
    audioHiUrl?: string
    model?: string
    error?: string
    _creationTime: number
  }
}) {
  const audioRef = useRef<HTMLAudioElement>(null)

  // Use unified audio player hook for coordination
  const { play, pause, isPlaying } = useNativeAudioPlayer({
    audioRef,
    audioId: generation._id,
    audioUrl: generation.audioUrl || '',
  })

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  }

  const togglePlay = () => {
    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium ${
            statusColors[generation.status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
          }`}
        >
          {generation.status}
        </span>
        <span className="text-xs text-gray-500">
          {new Date(generation._creationTime).toLocaleDateString()}
        </span>
      </div>

      {generation.prompt && (
        <p className="text-sm text-gray-700 mb-2 line-clamp-2">{generation.prompt}</p>
      )}

      {generation.model && (
        <p className="text-xs text-gray-500 mb-2">Model: {generation.model}</p>
      )}

      {generation.status === 'completed' && generation.audioUrl && (
        <div className="space-y-2">
          <button
            onClick={togglePlay}
            className="w-full bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
          >
            {isPlaying ? '⏸ Pause' : '▶ Play'}
          </button>

          <audio
            ref={audioRef}
            src={generation.audioUrl}
            className="sr-only"
          />

          <div className="flex gap-2">
            <a
              href={generation.audioUrl}
              download
              className="flex-1 text-center bg-gray-100 text-gray-700 px-3 py-2 rounded text-xs hover:bg-gray-200 transition-colors"
            >
              ⬇ MP3
            </a>
            {generation.audioHiUrl && (
              <a
                href={generation.audioHiUrl}
                download
                className="flex-1 text-center bg-gray-100 text-gray-700 px-3 py-2 rounded text-xs hover:bg-gray-200 transition-colors"
              >
                ⬇ WAV
              </a>
            )}
          </div>
        </div>
      )}

      {generation.status === 'failed' && generation.error && (
        <p className="text-xs text-red-600">{generation.error}</p>
      )}
    </div>
  )
}
